import { useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import { AdminUser } from '../auth/session';

/**
 * Admin auth service — login via **id (email) + password**.
 *
 * The admin console ships with mock data and (today) no admin backend, so by
 * default this validates against built-in mock credentials and mints a fake
 * token. Set REACT_APP_USE_MOCK_AUTH=false to call the real LOGIN endpoint
 * instead — the rest of the app (axios bearer injection, session storage,
 * route guards) is already wired for it.
 */

const USE_MOCK = (process.env.REACT_APP_USE_MOCK_AUTH ?? 'true') !== 'false';
const MOCK_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'admin@basequeue.com';
const MOCK_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  user: AdminUser;
}

export const useAuthService = () => {
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    if (USE_MOCK) {
      // Simulate network latency for a realistic UX.
      await new Promise((r) => setTimeout(r, 600));
      if (email.trim().toLowerCase() !== MOCK_EMAIL.toLowerCase() || password !== MOCK_PASSWORD) {
        const err: any = new Error('Invalid credentials');
        err.response = { data: { message: 'Invalid email or password.' } };
        throw err;
      }
      return {
        accessToken: 'mock-admin-token.' + btoa(email),
        refreshToken: 'mock-refresh-token',
        user: { id: 'ADM-0001', name: 'Super Admin', email, role: 'funserve_admin' },
      };
    }

    // Real backend: POST /api/v1/admin/auth/login → { data: { access_token, refresh_token, admin } }
    const response = await axiosInstance.post(API_ENDPOINTS.LOGIN, { email, password });
    const data = response.data?.data ?? response.data;
    const admin = data.admin ?? data.user;
    return {
      accessToken: data.access_token ?? data.accessToken,
      refreshToken: data.refresh_token ?? data.refreshToken ?? '',
      user: admin as AdminUser,
    };
  }, []);

  const setLoginDetails = useCallback((accessToken: string, refreshToken: string, user: AdminUser) => {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }, []);

  const isAuthenticated = useCallback((): boolean => !!localStorage.getItem('accessToken'), []);

  return { login, setLoginDetails, logout, isAuthenticated };
};
